import { useCallback, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'

import { getCalendarDays, getNextMonth, getPreviousMonth } from '@coorum-research/shared-common/utils/calendar.util'
import { formatDate } from '@coorum-research/shared-common/utils/date.util'
import { type CalendarDay, type DateAnswersMap, type YearMonth } from '@coorum-research/shared-common/types/calendar.type'

import { getCalendarAnswerCounts, getDateAnswers, getOldestAnswerDate } from '../apis/calendars.api'


export function useCalendar() {
  // デフォルトは当月
  const now = new Date()
  const [ yearMonth, setYearMonth ] = useState<YearMonth>({ 
    year: now.getFullYear(),
    month: now.getMonth() + 1
  })

  /**
   * {
   *   { '2023-05-23': [ { id: '01J1W37NGF', ... }] },
   *   { '2023-06-10': [ { id: '01J1W64NGF', ... }] },
   *   { '2023-07-05': [ { id: '01J1W30NGF', ... }] },
   * }
   */
  const [ dateAnswersMap, setDateAnswersMap ] = useState<DateAnswersMap>({})

  /**
   * 前月に移動 もしくは 前年の12月に移動
   */
  const handleBack = useCallback(() => {
    setYearMonth(prev => getPreviousMonth(prev.year, prev.month))
  }, [])

  /**
   * 翌月に移動 もしくは 翌年の1月に移動
   */
  const handleForward = useCallback(() => {
    setYearMonth(prev => getNextMonth(prev.year, prev.month))
  }, [])

  /**
   * クリックした日のアンケート回答リストを取得して、dateAnswersMap に assign
   */
  const handleGetDateAnswers = useCallback(async (day: CalendarDay) => {
    if (day === '') {
      return
    }

    const date = new Date(yearMonth.year, yearMonth.month - 1, day)
    const { items } = await getDateAnswers(date)
    setDateAnswersMap(prev => {
      const k = formatDate(date, 'yyyy-MM-dd')
      return {
        ...prev,
        [k]: items,
      }
    })
  }, [yearMonth.year, yearMonth.month])

  /**
   * 初回に、最古のアンケート回答日を得る
   */
  const { data: oldestAnswerDate } = useQuery({
    queryKey: ['get-calendars-oldest-answer-date'],
    queryFn: async () => {
      const { oldestAnswerDate } = await getOldestAnswerDate()
      return oldestAnswerDate
    },
    refetchOnWindowFocus: false,
  })

  /**
   * 年月変更時に、日毎のアンケート回答数を得る
   */
  const { data: groups } = useQuery({
    queryKey: ['get-calendars-answer-counts', yearMonth.year, yearMonth.month],
    queryFn: async () => {
      const { groups } = await getCalendarAnswerCounts(yearMonth.year, yearMonth.month)
      return groups
    },
    refetchOnWindowFocus: false,
  })

  /**
   * [
   *   { day: '', count: 0 },
   *   { day: 1,  count: 0 },
   *   { day: 2,  count: 1 },
   *   ...
   *   { day: 31, count: 3 },
   * ]
   */
  const dayCounts = useMemo(() => {
    const days = getCalendarDays(yearMonth.year, yearMonth.month)

    const groupsMapFormat: { [day: string]: number } = {}
    for (const g of groups ?? []) {
      const d = new Date(g.createdAt)
      groupsMapFormat[`${d.getDate()}`] = g.value
    }

    return days.map(d => {
      return d in groupsMapFormat ? { day: d, count: groupsMapFormat[`${d}`] } : { day: d, count: 0 }
    })
  }, [yearMonth.year, yearMonth.month, groups])

  return {
    yearMonth,
    dayCounts,
    dateAnswersMap,
    oldestAnswerDate,
    handleBack,
    handleForward,
    handleGetDateAnswers,
  }
}
